import React, { useState, useEffect, useCallback } from "react";
import Dialog from "@mui/material/Dialog";
import { RadioGroup, Radio, FormControlLabel, styled } from "@mui/material";
import * as S from "./styles";
import DeleteIcon from "assets/images/settings/delete.svg";
import CloseIcon from "@mui/icons-material/Close";
import Button from "../Button";
import AddPayment from "../Settings/AddPayment";
import { getAllCards } from "../../services/api";
import { useRemoveCard, useSetDefaultCard } from "../../services/mutations";
import { isCardExpired } from "../../utils/helpers";
import { CircularProgress } from "@mui/material";
import { toast } from "react-toastify";
import { colors } from "../../utils";
import { cardIcon } from "../Settings/Billing/helper";

const StyledRadio = styled(Radio)(() => ({
  "&.Mui-checked": {
    color: "#9B5AF8",
  },
}));

const PaymentMethods = ({
  open,
  handleClose,
  forcePopupClose,
  onCardChanged,
}) => {
  const [addNewStep, setAddNewStep] = useState(false);
  const [cards, setCards] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingCardId, setLoadingCardId] = useState(null);
  const [selectedCardId, setSelectedCardId] = useState(null);

  const removeCard = useRemoveCard();
  const setDefault = useSetDefaultCard();

  const fetchCards = useCallback(async () => {
    setFetching(true);
    const response = await getAllCards();
    if (!response.data.cards?.length) {
      setFetching(false);
      setAddNewStep(true);
    }
    const cardsData = response.data.cards || [];
    setCards(cardsData);
    // Set the default card as selected, or first card if no default
    const defaultCard = cardsData.find((card) => card.is_default);
    setSelectedCardId(
      defaultCard
        ? defaultCard.id
        : cardsData.length > 0
        ? cardsData[0].id
        : null
    );
    setFetching(false);
    // Notify parent component about card changes
    onCardChanged && onCardChanged(cardsData);
  }, [onCardChanged]);

  useEffect(() => {
    fetchCards();
  }, []);

  const handleCardDelete = async (cardId) => {
    setLoading(true);
    removeCard.mutateAsync(
      {
        pm_id: cardId,
      },
      {
        onSuccess: () => {
          const filteredCards = cards.filter((card) => card.id !== cardId);
          setLoading(false);
          setCards(filteredCards);
          // If we deleted the selected card, select another one
          if (selectedCardId === cardId) {
            const defaultCard = filteredCards.find((card) => card.is_default);
            setSelectedCardId(
              defaultCard
                ? defaultCard.id
                : filteredCards.length > 0
                ? filteredCards[0].id
                : null
            );
          }

          // Notify parent about card changes, including if all cards were deleted
          onCardChanged && onCardChanged(filteredCards);
        },
        onError: (err) => {
          setLoading(false);
          console.log({ err });
        },
      }
    );
  };

  const setDefaultCard = () => {
    if (!selectedCardId) return;

    setLoadingCardId(selectedCardId);
    setDefault.mutateAsync(
      {
        payment_method: selectedCardId,
      },
      {
        onSuccess: (res) => {
          toast.success("Card set as default.");
          const filtered = cards.map((card) => {
            return {
              ...card,
              is_default: card.id === selectedCardId,
            };
          });
          setCards(filtered);
          setLoadingCardId(null);
          onCardChanged && onCardChanged(filtered);
        },
        onError: (err) => {
          console.log(err);
          setLoadingCardId(null);
        },
      }
    );
  };

  const handleRadioChange = (event) => {
    setSelectedCardId(event.target.value);
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <S.DialogContainer>
        <S.StyledDialogTitle>
          <S.Title>Payment Methods</S.Title>
          <CloseIcon className="close-icon" onClick={handleClose} />
        </S.StyledDialogTitle>

        <S.Separator />
        {fetching && (
          <S.LoadingContainer>
            <CircularProgress />
          </S.LoadingContainer>
        )}
        {!fetching && (
          <S.StyledDialogContent>
            {!addNewStep ? (
              cards.length ? (
                <RadioGroup
                  value={selectedCardId}
                  onChange={handleRadioChange}
                  style={{ width: "100%" }}
                >
                  {cards?.map((card) => {
                    const isExpired = isCardExpired(
                      card.expiry_month,
                      card.expiry_year
                    );
                    return (
                      <S.CardItem
                        isDefaultCard={card.is_default}
                        key={card.id}
                        style={{ marginBottom: "16px" }}
                      >
                        <S.PaymentMethodItem>
                          <FormControlLabel
                            value={card.id}
                            control={
                              <StyledRadio
                                checked={selectedCardId === card.id}
                              />
                            }
                            label={
                              <S.CardDetails>
                                <S.CardLogo
                                  src={cardIcon[card.brand]}
                                  alt={card.brand}
                                />
                                <S.CardInfo>
                                  <S.CardNumber>
                                    •••• •••• •••• {card.last4}
                                  </S.CardNumber>
                                  <S.CardExpiry>
                                    Exp. date {card.expiry_month}/
                                    {card.expiry_year}
                                  </S.CardExpiry>
                                </S.CardInfo>
                              </S.CardDetails>
                            }
                          />
                          <S.ButtonsContainer>
                            {isExpired ? (
                              <S.ExpireButton>Expired</S.ExpireButton>
                            ) : (
                              card.is_default && (
                                <S.DefaultButton>Default</S.DefaultButton>
                              )
                            )}
                            {loading ? (
                              <CircularProgress
                                size={20}
                                style={{ marginLeft: "5px" }}
                              />
                            ) : (
                              <img
                                src={DeleteIcon}
                                alt="delete"
                                width={24}
                                height={24}
                                style={{ cursor: "pointer" }}
                                onClick={() => handleCardDelete(card.id)}
                              />
                            )}
                          </S.ButtonsContainer>
                        </S.PaymentMethodItem>
                      </S.CardItem>
                    );
                  })}
                </RadioGroup>
              ) : (
                <S.InternalHeading>
                  Please add a new payment method.
                </S.InternalHeading>
              )
            ) : (
              <AddPayment
                closeModal={() => {
                  setAddNewStep(false);
                  if (forcePopupClose || cards.length === 0) {
                    handleClose();
                  } else {
                    fetchCards();
                  }
                }}
              />
            )}

            <S.BottomSeparator>
              <S.Separator />
            </S.BottomSeparator>

            {!addNewStep && (
              <S.Footer>
                <Button
                  variant={"outlined"}
                  onClick={() => setAddNewStep(true)}
                  style={{
                    border: `1px solid ${colors.PrimaryPurple600}`,
                    width: "auto",
                    whiteSpace: "nowrap",
                  }}
                >
                  New Payment Method
                </Button>
                {cards.length > 0 && (
                  <Button
                    variant={"primary"}
                    onClick={setDefaultCard}
                    disabled={!selectedCardId || loadingCardId !== null}
                    style={{
                      border: `1px solid ${colors.PrimaryPurple600}`,
                      marginLeft: "10px",
                      width: "auto",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {loadingCardId !== null ? (
                      <CircularProgress
                        size={20}
                        style={{ color: colors.PrimaryPurple600 }}
                      />
                    ) : (
                      "Set as Default"
                    )}
                  </Button>
                )}
              </S.Footer>
            )}
          </S.StyledDialogContent>
        )}
      </S.DialogContainer>
    </Dialog>
  );
};

export default PaymentMethods;
